// src/pages/CartPage.tsx
// -----------------------------------------------------------------------------
// The cart, config-driven. This is the Phase-3 template: nothing here is
// Red-Robin-specific. Shipping regions, options and the pickup choice come from
// smartshop-shop-config - the SAME record checkout-v2 prices from - so a shop can
// change its shipping in Firestore with no storefront redeploy.
//
// Checkout posts to smartshop-checkout-v2 with only {cart_id, region, pickup}.
// The tenant is taken from the stored cart, never sent by the storefront (D-081).
//
// Guidance seam (future Ray-Dee): each decision point below - shipping choice,
// "not ready to buy", empty cart - is a place the advisor can speak into later.
//
// House rule: short hyphen "-" only.
// -----------------------------------------------------------------------------
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, SMARTSHOP_API, CLIENT_ID } from '../firebaseConfig';
import { shopConfig as cfg } from '../shopConfig';
import { useBrand } from '../hooks/useBrand';
import { useCart } from '../hooks/useCart';
import { useAuth } from '../hooks/useAuth';

interface ShipOption {
  code: string; label: string; amount: number;
  min_days: number | null; max_days: number | null;
}
interface ShipRegion { code: string; label: string; options: ShipOption[] }
interface ShopCfg {
  currency: string; default_region: string;
  regions: ShipRegion[];
  pickup: { code: string; label: string; amount: number } | null;
}

const money = (cents: number | null | undefined) =>
  cents == null ? '' : new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR' }).format(cents / 100);

const estimate = (o: ShipOption) => {
  if (o.min_days == null || o.max_days == null) return '';
  return o.min_days === o.max_days ? `${o.min_days} working days` : `${o.min_days}-${o.max_days} working days`;
};

export const CartPage: React.FC = () => {
  const brand = useBrand();
  const accent = brand.accent || cfg.brand.accent;
  const navigate = useNavigate();
  const { cart, loadCart, error, updateItem } = useCart();
  const { signedIn } = useAuth();

  const [shop, setShop] = useState<ShopCfg | null>(null);
  const [region, setRegion] = useState<string>('');
  const [pickup, setPickup] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => { loadCart(); }, [loadCart]);

  // shipping choices from the tenant's own config - one source of truth with checkout
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${SMARTSHOP_API}/smartshop-shop-config?gido_id=${CLIENT_ID}`);
        if (!res.ok) return;
        const data: ShopCfg = await res.json();
        if (!alive) return;
        setShop(data);
        setRegion(data.default_region);
      } catch { /* cart still works; shipping falls back to the backend default */ }
    })();
    return () => { alive = false; };
  }, []);

  const handleSave = async () => {
    if (!cart?.cart_id) return;
    setSaving(true); setSaveError(null);
    try {
      const res = await fetch(`${SMARTSHOP_API}/smartshop-cart-save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          gido_id: CLIENT_ID,
          cart_id: cart.cart_id,
          session_id: localStorage.getItem('smartshop_session_id'),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not save your cart');
      setSaved(true);
    } catch (e: any) {
      setSaveError(e.message);
    } finally { setSaving(false); }
  };

  const handleCheckout = async () => {
    if (!cart?.cart_id) return;
    setCheckingOut(true); setCheckoutError(null);
    try {
      const user = auth.currentUser;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (user) headers['Authorization'] = `Bearer ${await user.getIdToken()}`;
      const res = await fetch(`${SMARTSHOP_API}/smartshop-checkout-v2`, {
        method: 'POST', headers,
        body: JSON.stringify({ cart_id: cart.cart_id, region, pickup }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.reasons ? data.reasons.join(', ') : data.error || 'Checkout failed');
      window.location.href = data.url;
    } catch (e: any) {
      setCheckoutError(e.message);
    } finally { setCheckingOut(false); }
  };

  const card: React.CSSProperties = {
    background: '#fff', border: '1px solid rgba(0,0,0,.08)',
    boxShadow: '0 2px 10px rgba(0,0,0,.08)', padding: 18, marginBottom: 18,
  };
  const label: React.CSSProperties = {
    fontSize: 11, textTransform: 'uppercase', letterSpacing: '.12em',
    color: 'rgba(0,0,0,.45)', margin: '0 0 10px',
  };

  if (!cart) {
    return <div style={{ padding: 60, textAlign: 'center', color: 'rgba(0,0,0,.4)' }}>Loading your cart...</div>;
  }

  if (!cart.items.length) {
    return (
      <div style={{ maxWidth: 480, margin: '60px auto', padding: '0 20px', textAlign: 'center' }}>
        <h1 style={{ fontSize: 24, fontWeight: 900, fontStyle: 'italic', color: cfg.brand.secondary, marginBottom: 12 }}>
          Your Cart is Empty
        </h1>
        <p style={{ color: 'rgba(0,0,0,.5)', fontSize: 14, marginBottom: 20 }}>
          Have a look around - there is plenty to discover.
        </p>
        <button onClick={() => navigate('/shop')}
          style={{ background: accent, color: cfg.brand.secondary, border: 'none', padding: '14px 28px', fontWeight: 800, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.12em', cursor: 'pointer' }}>
          Browse the Shop
        </button>
      </div>
    );
  }

  const activeRegion = shop?.regions.find(r => r.code === region) || null;

  return (
    <div style={{ maxWidth: 620, margin: '32px auto', padding: '0 16px 56px' }}>
      <h1 style={{ fontSize: 'clamp(22px,5vw,30px)', fontWeight: 900, fontStyle: 'italic', color: cfg.brand.secondary, margin: '0 0 18px' }}>
        Your Cart
      </h1>

      {cart.merge_notice && (
        <p style={{ fontSize: 12, color: 'rgba(0,0,0,.6)', background: 'rgba(0,0,0,.05)', padding: '10px 14px', marginBottom: 16 }}>
          You already had a saved cart from last visit - we have combined them.
        </p>
      )}

      {/* items */}
      <div style={card}>
        {cart.items.map((item, idx) => {
          const unit = (item.price_type === 'variable' ? item.variable_amount : item.price_snapshot) || 0;
          const isVariable = item.price_type === 'variable';
          return (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: idx === cart.items.length - 1 ? 'none' : '1px solid rgba(0,0,0,.08)' }}>
              {item.image_snapshot
                ? <img src={item.image_snapshot} alt={item.name_snapshot}
                    onClick={() => item.slug && navigate(`/product/${item.slug}`)}
                    style={{ width: 56, height: 70, objectFit: 'cover', flex: '0 0 auto', background: '#f2efe6', cursor: item.slug ? 'pointer' : 'default' }} />
                : <div style={{ width: 56, height: 70, background: '#f2efe6', flex: '0 0 auto' }} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <p onClick={() => item.slug && navigate(`/product/${item.slug}`)}
                  style={{ margin: 0, fontSize: 14, fontWeight: 600, color: cfg.brand.secondary, cursor: item.slug ? 'pointer' : 'default' }}>
                  {item.name_snapshot}{item.variant_name_snapshot ? ` - ${item.variant_name_snapshot}` : ''}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: 'rgba(0,0,0,.5)' }}>{money(unit)} each</p>
              </div>
              {!isVariable && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <button aria-label="Decrease quantity" disabled={item.qty <= 1}
                    onClick={() => updateItem(item.product_id, item.qty - 1, item.variant_id || undefined)}
                    style={{ width: 28, height: 28, background: 'rgba(0,0,0,.04)', border: '1px solid rgba(0,0,0,.2)', cursor: item.qty <= 1 ? 'not-allowed' : 'pointer', fontSize: 16, lineHeight: 1, opacity: item.qty <= 1 ? .3 : 1 }}>
                    &minus;
                  </button>
                  <span style={{ fontSize: 14, minWidth: 20, textAlign: 'center' }}>{item.qty}</span>
                  <button aria-label="Increase quantity"
                    onClick={() => updateItem(item.product_id, item.qty + 1, item.variant_id || undefined)}
                    style={{ width: 28, height: 28, background: 'rgba(0,0,0,.04)', border: '1px solid rgba(0,0,0,.2)', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>
                    +
                  </button>
                </div>
              )}
              <span style={{ fontSize: 14, fontWeight: 800, color: accent, minWidth: 70, textAlign: 'right' }}>
                {money(unit * item.qty)}
              </span>
              <button aria-label="Remove item"
                onClick={() => updateItem(item.product_id, 0, item.variant_id || undefined)}
                style={{ background: 'none', border: 'none', color: 'rgba(0,0,0,.3)', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 2px' }}>
                &times;
              </button>
            </div>
          );
        })}
      </div>

      {/* delivery - driven entirely by the tenant's own shop_config */}
      {shop && (
        <div style={card}>
          <p style={label}>Delivery</p>
          {shop.pickup && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={pickup} onChange={e => setPickup(e.target.checked)} />
              <span style={{ fontSize: 13, color: cfg.brand.secondary }}>
                {shop.pickup.label}{shop.pickup.amount === 0 ? ' - free' : ` - ${money(shop.pickup.amount)}`}
              </span>
            </label>
          )}
          {!pickup && (
            <>
              <select value={region} onChange={e => setRegion(e.target.value)}
                style={{ width: '100%', padding: 10, background: '#fff', border: '1px solid rgba(0,0,0,.2)', fontSize: 13, fontFamily: 'inherit' }}>
                {shop.regions.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
              </select>
              {activeRegion && activeRegion.options.length > 0 && (
                <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0 0' }}>
                  {activeRegion.options.map(o => (
                    <li key={o.code} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'rgba(0,0,0,.6)', padding: '4px 0' }}>
                      <span>{o.label}{estimate(o) ? ` - ${estimate(o)}` : ''}</span>
                      <span>{money(o.amount)}</span>
                    </li>
                  ))}
                  <li style={{ fontSize: 11, color: 'rgba(0,0,0,.45)', paddingTop: 6 }}>
                    You choose your delivery option on the payment page.
                  </li>
                </ul>
              )}
            </>
          )}
        </div>
      )}

      {/* save for later / register */}
      <div style={{ ...card, background: 'rgba(0,0,0,.03)' }}>
        {signedIn ? (
          saved ? (
            <p style={{ margin: 0, fontSize: 13, color: cfg.brand.secondary }}>
              Saved. It will be here next time you sign in.
            </p>
          ) : (
            <>
              <p style={{ margin: '0 0 10px', fontSize: 13, color: cfg.brand.secondary }}>
                Not ready to buy? Save this cart and pick it up later, on any device.
              </p>
              <button onClick={handleSave} disabled={saving}
                style={{ background: 'none', color: cfg.brand.secondary, border: `1px solid ${accent}`, padding: '10px 18px', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.1em', cursor: saving ? 'wait' : 'pointer', opacity: saving ? .6 : 1 }}>
                {saving ? 'Saving...' : 'Save for Later'}
              </button>
            </>
          )
        ) : (
          <>
            <p style={{ margin: '0 0 10px', fontSize: 13, color: cfg.brand.secondary }}>
              Want to save this cart for later? Create an account - your cart comes with you.
            </p>
            <button onClick={() => navigate('/account?return=/cart')}
              style={{ background: accent, color: cfg.brand.secondary, border: 'none', padding: '10px 18px', fontWeight: 800, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.1em', cursor: 'pointer' }}>
              Register &amp; Save My Cart
            </button>
          </>
        )}
        {saveError && <p style={{ color: '#b3261e', fontSize: 12, margin: '10px 0 0' }}>{saveError}</p>}
      </div>

      {/* total + checkout */}
      <div style={{ borderTop: '1px solid rgba(0,0,0,.1)', paddingTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: 14, color: 'rgba(0,0,0,.6)' }}>Subtotal</span>
          <span style={{ fontSize: 14, fontWeight: 800, color: cfg.brand.secondary }}>{money(cart.total)}</span>
        </div>
        <p style={{ fontSize: 12, color: 'rgba(0,0,0,.45)', margin: '0 0 16px' }}>
          Shipping is added at checkout - the final total is shown on the payment page.
        </p>

        {checkoutError && <p style={{ color: '#b3261e', fontSize: 12, marginBottom: 12 }}>{checkoutError}</p>}
        {error && <p style={{ color: '#b3261e', fontSize: 12, marginBottom: 12 }}>{error}</p>}

        <button onClick={handleCheckout} disabled={checkingOut}
          style={{ width: '100%', background: accent, color: cfg.brand.secondary, border: 'none', padding: 16, fontWeight: 800, fontSize: 13, textTransform: 'uppercase', letterSpacing: '.14em', cursor: checkingOut ? 'wait' : 'pointer', opacity: checkingOut ? .6 : 1 }}>
          {checkingOut ? 'Redirecting to payment...' : 'Proceed to Payment'}
        </button>
        <p style={{ fontSize: 10, color: 'rgba(0,0,0,.35)', textAlign: 'center', margin: '10px 0 0', textTransform: 'uppercase', letterSpacing: '.08em' }}>
          Secure payment via Stripe
        </p>
      </div>
    </div>
  );
};

export default CartPage;